import { useMemo, useState } from "react";

import { faArrowUpRight, faTimes } from "@fortawesome/pro-regular-svg-icons";
import { useClose } from "@headlessui/react";
import { endOfToday, startOfToday, subDays } from "date-fns";
import { twJoin } from "tailwind-merge";

import { DecisionEnvironment } from "src/api/types";
import { DateRange } from "src/base-components/DateRangePicker";
import { Pill } from "src/base-components/Pill";
import { CustomPopover } from "src/base-components/Popover";
import { FallbackInput } from "src/cases/common/FallbackInput";
import { ResourceSearchPane } from "src/datasets/components/ResourceSearchPane";
import { IconButton } from "src/design-system/Icon";
import { FIELD_BORDER } from "src/design-system/utils/fieldStyles";
import {
  useEvent,
  useEventSchemas,
  useEvents,
} from "src/eventsCatalogue/queries";
import { findEventSchema, getEventIcon } from "src/eventsCatalogue/utils";
import { useEnvironment } from "src/hooks/useEnvironment";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { getBaseUrl, getUrlToEventsPage } from "src/router/urls";
import { formatDate } from "src/utils/datetime";
import { ErrorBoundary } from "src/utils/ErrorBoundary";
import { useTimeWindow } from "src/utils/timeWindow";

const EVENT_TIMESTAMP_FORMAT = "MMM d, yyyy 'at' h:mm a";

type SearchableEventRefInputProps = {
  eventType: string;
  value: string;
  onChange: (id: string) => void;
  errored: boolean;
};

export const SearchableEventRefInput: React.FC<SearchableEventRefInputProps> = (
  props,
) => (
  <ErrorBoundary
    fallbackErrorContent={
      <FallbackInput
        placeholder="Search failed, please enter Event ID manually"
        onCommit={props.onChange}
      />
    }
  >
    <SearchableEventRefInputInner {...props} />
  </ErrorBoundary>
);

const SearchableEventRefInputInner: React.FC<SearchableEventRefInputProps> = ({
  eventType,
  value,
  onChange,
  errored,
}) => {
  const [pageEnv] = useEnvironment();
  const env = pageEnv as DecisionEnvironment;
  const [searchQuery, setSearchQuery] = useState("");

  const { data: eventSchemas } = useEventSchemas();
  const eventSchema = findEventSchema(eventType, eventSchemas);
  const eventIcon = eventSchema ? getEventIcon(eventSchema) : undefined;

  const { orgId, workspace } = useWorkspaceContext();

  const { onDateRangePickerChange, timeWindow, dateRangePickerValue } =
    useTimeWindow(
      {
        from: subDays(startOfToday(), 7),
        to: endOfToday(),
      },
      "case-events-time-window",
    );

  const userIsSearching = searchQuery.length > 0;

  const { data: eventsData, isLoading: isEventsLoading } = useEvents(
    {
      eventType,
      environment: env,
      timeWindow: {
        from: timeWindow.from?.toISOString(),
        to: timeWindow.to?.toISOString(),
      },
    },
    {
      enabled: !userIsSearching,
    },
  );

  const { data: eventData, isLoading: isEventLoading } = useEvent(
    {
      eventType,
      eventId: searchQuery,
      environment: env,
      timeWindow: {
        from: timeWindow.from?.toISOString(),
        to: timeWindow.to?.toISOString(),
      },
    },
    {
      enabled: userIsSearching,
    },
  );

  const { data: selectedEvent } = useEvent({
    eventType,
    eventId: value || undefined,
    environment: env,
    timeWindow: {
      from: timeWindow.from?.toISOString(),
      to: timeWindow.to?.toISOString(),
    },
  });

  // Resolve display label from dedicated query or search results already in memory
  const pillLabel = useMemo(() => {
    const event =
      selectedEvent ??
      (eventData?.payload.id === value ? eventData : undefined) ??
      eventsData?.find((e) => e.payload.id === value);
    if (!event) return null;
    const timestamp = formatDate(
      event.payload.timestamp,
      EVENT_TIMESTAMP_FORMAT,
    );
    return `${event.payload.id} - ${timestamp}`;
  }, [selectedEvent, value, eventData, eventsData]);

  const items = useMemo(() => {
    const data = userIsSearching
      ? eventData
        ? [eventData]
        : []
      : (eventsData ?? []);

    return data.map((event) => ({
      label: formatDate(event.payload.timestamp, EVENT_TIMESTAMP_FORMAT),
      key: event.payload.id,
      isFetching: false,
    }));
  }, [eventData, userIsSearching, eventsData]);

  const isLoading = userIsSearching ? isEventLoading : isEventsLoading;
  const displayName =
    eventSchema?.display_name_plural ??
    eventSchema?.display_name_singular ??
    "Events";

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
  };

  const eventUrl = value
    ? getBaseUrl() +
      getUrlToEventsPage({
        orgId,
        wsId: workspace.id,
        eventType,
        eventId: value,
      })
    : undefined;

  return (
    <CustomPopover
      button={
        <div
          className={twJoin(
            "flex h-8 w-full items-center rounded-lg border bg-white px-3 text-left font-inter-normal-12px focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/25",
            !errored && FIELD_BORDER.default,
            errored && FIELD_BORDER.errored,
          )}
          data-loc="event-ref-value"
          tabIndex={0}
        >
          {value ? (
            <div className="flex w-full min-w-0 items-center gap-1">
              <a
                className="min-w-0"
                href={eventUrl}
                rel="noreferrer"
                target="_blank"
                onClick={(e) => e.stopPropagation()}
              >
                <Pill size="sm" variant="gray" maxWidth>
                  {eventIcon && <Pill.Icon icon={eventIcon} />}
                  <Pill.Text>{pillLabel || value}</Pill.Text>
                  <Pill.Icon icon={faArrowUpRight} />
                </Pill>
              </a>
              <div className="ml-auto shrink-0">
                <IconButton icon={faTimes} size="xs" onClick={handleClear} />
              </div>
            </div>
          ) : (
            <span className="text-gray-500">Search Event</span>
          )}
        </div>
      }
      placement="bottom-start"
      wrapperClassName="w-full"
    >
      <EventSearchPaneContent
        dateRangePickerValue={dateRangePickerValue}
        displayName={displayName}
        env={env}
        isLoading={isLoading}
        items={items}
        searchQuery={searchQuery}
        onChange={onChange}
        onDateRangePickerChange={onDateRangePickerChange}
        onSearchQueryChange={setSearchQuery}
      />
    </CustomPopover>
  );
};

const EventSearchPaneContent: React.FC<{
  env: DecisionEnvironment;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  isLoading: boolean;
  items: { label: string; key: string; isFetching: boolean }[];
  displayName: string;
  dateRangePickerValue: DateRange | undefined;
  onDateRangePickerChange: (value: DateRange | undefined) => void;
  onChange: (id: string) => void;
}> = ({
  env,
  searchQuery,
  onSearchQueryChange,
  isLoading,
  items,
  displayName,
  dateRangePickerValue,
  onDateRangePickerChange,
  onChange,
}) => {
  const close = useClose();

  return (
    <div className="w-134">
      <ResourceSearchPane
        filters={{
          environment: env,
          searchQuery,
          timeWindow: dateRangePickerValue,
        }}
        hideEnvironmentFilter
        isEvent
        isLoading={isLoading}
        items={items}
        resourceName={displayName}
        searchPlaceholder="Search by Event ID"
        onChangeFilters={(filters) => {
          onSearchQueryChange(filters.searchQuery);
          onDateRangePickerChange(filters.timeWindow);
        }}
        onSelect={(item) => {
          onChange(item.key);
          close();
        }}
      />
    </div>
  );
};
